/** Skládání MQTT vztahů (klient ↔ most ↔ téma) z EMQX dat. */

export type MqttKind = 'bridge' | 'mcp' | 'fleet' | 'api' | 'other';

export interface MqttClientRow {
  clientid: string;
  username?: string;
  ip_address?: string;
  port?: number;
  protocol?: string;
  connected?: boolean;
  connected_at?: string;
  disconnected_at?: string;
  keepalive?: number;
  is_bridge?: boolean;
  subscriptions_cnt?: number;
  expected_topics?: string[];
  missing_topics?: string[];
  last_contact?: string | null;
  observed_at?: string;
  relationship?: 'cluster' | 'spoke' | 'acm_link' | 'unknown';
  label?: string;
  stable_id?: string;
  location?: { lat: number; lon: number; source: string; recorded_at: string } | null;
}

export interface MqttSubRow {
  clientid: string;
  topic: string;
  qos?: number;
}

export interface MqttBridgeRow {
  name: string;
  connected?: boolean;
  status?: string;
  ingress_total?: number;
  ingress_running?: number;
  ingress_names?: string[];
  status_reason?: string;
  expected_topics?: string[];
  missing_topics?: string[];
  egress_total?: number;
  egress_running?: number;
  diagnostic_status?: 'ok' | 'error' | 'unknown';
}

export function mqttClientId(c: Record<string, unknown> | MqttClientRow): string {
  return String((c as MqttClientRow).clientid || (c as { client_id?: string }).client_id || '');
}

export function mqttKind(clientid: string, isBridge?: boolean): MqttKind {
  if (isBridge || clientid.startsWith('bridge-')) return 'bridge';
  if (clientid.startsWith('mcp-proxy')) return 'mcp';
  if (clientid.startsWith('acm-fleet')) return 'fleet';
  if (clientid.startsWith('acm-api')) return 'api';
  return 'other';
}

/** `bridge-{from}-to-{to}…` → odkud kam. */
export function parseBridgeClient(clientid: string): { from: string; to: string } | null {
  const m = clientid.match(/^bridge-(.+?)-to-(.+?)(?:hub_link|peer_|:|$)/);
  if (!m?.[1] || !m[2]) return null;
  return { from: m[1], to: m[2] };
}

export function topicFamily(topic: string): string {
  if (topic.startsWith('acm/catalog/')) return 'katalog';
  if (topic.startsWith('acm/mesh/')) return 'mesh';
  if (topic.startsWith('acm/fleet/')) return 'fleet';
  if (topic.startsWith('acm/notify/')) return 'oznámení';
  if (topic.startsWith('agents/')) return 'agenti';
  return 'ostatní';
}

export function shortNode(id: string): string {
  if (!id) return '—';
  if (/^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(id)) return id.slice(0, 8);
  return id;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const CLIENT_PREFIX_RE = /^(mcp-proxy-ops-|mcp-proxy-|acm-api-|acm-fleet-|bridge-)/i;

export function looksRawId(value: string): boolean {
  const s = value.trim();
  if (!s) return true;
  if (UUID_RE.test(s)) return true;
  if (/^acm-[0-9a-f]{8,}$/i.test(s)) return true;
  return false;
}

function pushKey(out: string[], value?: string) {
  const v = (value ?? '').trim();
  if (!v || out.includes(v)) return;
  out.push(v);
}

/** UUID, hostname a familiar fragmenty z clientid/username. */
export function clientIdentityKeys(c: MqttClientRow): string[] {
  const out: string[] = [];
  pushKey(out, c.username);
  pushKey(out, c.username?.replace(CLIENT_PREFIX_RE, ''));
  const id = mqttClientId(c);
  const bridge = parseBridgeClient(id);
  if (bridge) {
    pushKey(out, bridge.from);
  }
  const rest = id.replace(CLIENT_PREFIX_RE, '');
  const uuid = rest.match(
    /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i,
  );
  if (uuid) pushKey(out, uuid[0]);
  else {
    const host = rest.split(/hub_link|peer_|[:/]/)[0]?.replace(/-to-.*$/, '') ?? '';
    pushKey(out, host);
  }
  return out;
}

export function connectorIdentityKeys(name: string): string[] {
  const raw = String(name ?? '').trim();
  if (!raw) return [];
  const out: string[] = [];
  pushKey(out, raw);
  pushKey(out, raw.replace(/^(peer_|bridge_)/, ''));
  return out;
}

export type LabelOf = (key: string) => string;

export function clientFamiliarName(c: MqttClientRow, labelOf: LabelOf): string {
  return familiarFromKeys(clientIdentityKeys(c), labelOf, shortNode(mqttClientId(c)));
}

export function connectorFamiliarName(name: string, labelOf: LabelOf): string {
  return familiarFromKeys(connectorIdentityKeys(name), labelOf, name || '—');
}

function isConnectorCode(value: string): boolean {
  return /^(peer_|bridge_|hub_link)/i.test(value.trim());
}

function familiarFromKeys(keys: string[], labelOf: LabelOf, fallback: string): string {
  for (const key of keys) {
    const labeled = (labelOf(key) || '').trim();
    if (labeled && !looksRawId(labeled) && !isConnectorCode(labeled)) return labeled;
  }
  for (const key of keys) {
    if (key && !looksRawId(key) && !isConnectorCode(key) && key.length < 48) return key;
  }
  return fallback;
}

export function clientContactIso(c: MqttClientRow | undefined | null): string | null {
  if (!c) return null;
  const iso = c.last_contact || (c.connected === false ? c.disconnected_at : null);
  return iso && iso !== 'null' ? iso : null;
}

export function contactAgeSeconds(
  iso: string | null | undefined,
  now = Date.now(),
): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (!Number.isFinite(t)) return null;
  return Math.max(0, Math.floor((now - t) / 1000));
}

export function isContactStale(seconds: number | null, local = false): boolean {
  if (local) return false;
  return seconds == null || seconds > 120;
}

export function clientMatchesPeerKeys(c: MqttClientRow, keys: string[]): boolean {
  if (keys.length === 0) return false;
  const ids = c.username ? [c.username, c.username.replace(CLIENT_PREFIX_RE, '')] : [];
  return ids.some((id) => keys.includes(id));
}

export function groupSubsByTopic(subs: MqttSubRow[]): Array<{
  topic: string;
  family: string;
  qos: number;
  clients: string[];
}> {
  const map = new Map<string, { topic: string; family: string; qos: number; clients: string[] }>();
  for (const s of subs) {
    const topic = String(s.topic ?? '');
    if (!topic) continue;
    const cur = map.get(topic) ?? {
      topic,
      family: topicFamily(topic),
      qos: Number(s.qos ?? 0),
      clients: [],
    };
    const id = String(s.clientid ?? '');
    if (id && !cur.clients.includes(id)) cur.clients.push(id);
    map.set(topic, cur);
  }
  return [...map.values()].sort((a, b) =>
    b.clients.length - a.clients.length || a.topic.localeCompare(b.topic)
  );
}

export function subsForClient(subs: MqttSubRow[], clientid: string): MqttSubRow[] {
  return subs.filter((s) => s.clientid === clientid);
}

export function classifyClients(clients: MqttClientRow[]): {
  bridgesIn: MqttClientRow[];
  local: MqttClientRow[];
} {
  const bridgesIn: MqttClientRow[] = [];
  const local: MqttClientRow[] = [];
  for (const c of clients) {
    const id = mqttClientId(c);
    if (mqttKind(id, c.is_bridge) === 'bridge') bridgesIn.push(c);
    else local.push(c);
  }
  return { bridgesIn, local };
}

/** Typ vztahu je inventář, ne typ MQTT klienta ani jeho volitelný label. */
export function peerRelationship(
  peer: { is_local?: boolean; peer_type?: string; role?: string | null },
): 'cluster' | 'spoke' | 'acm_link' | 'unknown' {
  if (peer.role === 'remote_operator' || peer.role === 'acm_link') return 'acm_link';
  if (peer.is_local || peer.peer_type === 'local' || peer.peer_type === 'cluster') return 'cluster';
  if (peer.peer_type === 'spoke' || peer.role === 'spoke' || peer.role === 'hub') return 'spoke';
  if (['peer', 'mesh-peer', 'cluster', 'self'].includes(peer.role ?? '')) return 'cluster';
  return 'unknown';
}

export function clientOnline(c: MqttClientRow): boolean {
  return c.connected === true;
}

export function peerMqttStatus(
  peer: { enabled?: boolean; is_local?: boolean; online?: boolean; last_seen?: string | null },
  clients: MqttClientRow[],
): 'running' | 'offline' | 'error' | 'unknown' {
  if (peer.enabled === false) return 'offline';
  if (clients.some((c) => c.missing_topics?.length)) return 'error';
  if (clients.some(clientOnline)) return 'running';
  if (clients.some((c) => c.connected === false)) return 'error';
  if (peer.is_local) return 'unknown';
  if (peer.online === false) return 'error';
  return 'unknown';
}
